import { api } from '../utils/api';

class LabelService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get labels for a specific language
   * @param {string} language - Language code (e.g., 'en', 'th')
   * @param {boolean} forceRefresh - Force refresh from server
   * @returns {Promise<Object>} Labels object
   */
  async getLabels(language = 'en', forceRefresh = false) {
    const cacheKey = `labels_${language}`;
    const cached = this.cache.get(cacheKey);
    
    // Return cached data if available and not expired
    if (!forceRefresh && cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    try {
      const response = await api.get(`/labels/${language}`);
      if (response.success) {
        // Cache the data
        this.cache.set(cacheKey, {
          data: response.data,
          timestamp: Date.now()
        });
        return response.data;
      } else {
        throw new Error('Failed to load labels');
      }
    } catch (error) {
      console.error(`Failed to load labels for language ${language}:`, error);
      
      // Check if it's an authentication error
      if (error.response?.status === 401 || error.response?.data?.code === 'TOKEN_MISSING') {
        console.log(`🔐 Authentication required for labels, using fallback for ${language}`);
        return null; // Return null to indicate fallback should be used
      }
      
      // Return cached data if available, even if expired
      if (cached) {
        return cached.data;
      }
      // Return null to indicate fallback should be used
      return null;
    }
  }

  /**
   * Get specific label value
   * @param {string} key - Label key (e.g., 'nav.dashboard')
   * @param {string} language - Language code
   * @param {Object} fallbackLabels - Fallback labels object
   * @returns {Promise<string>} Label value
   */
  async getLabel(key, language = 'en', fallbackLabels = {}) {
    try {
      const labels = await this.getLabels(language);
      const keys = key.split('.');
      let value = labels;
      
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          // Try fallback labels
          value = fallbackLabels;
          for (const fallbackKey of keys) {
            if (value && typeof value === 'object' && fallbackKey in value) {
              value = value[fallbackKey];
            } else {
              return key; // Return key if not found
            }
          }
          break;
        }
      }
      
      return typeof value === 'string' ? value : key;
    } catch (error) {
      console.error(`Failed to get label ${key} for language ${language}:`, error);
      return key;
    }
  }

  /**
   * Get all available languages
   * @returns {Promise<Array>} Array of language codes
   */
  async getAvailableLanguages() {
    try {
      const response = await api.get('/labels/languages');
      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to load languages');
      }
    } catch (error) {
      console.error('Failed to load available languages:', error);
      return ['en'];
    }
  }

  /**
   * Update a specific label
   * @param {string} language - Language code
   * @param {string} key - Label key
   * @param {string} value - New label value
   * @returns {Promise<boolean>} Success status
   */
  async updateLabel(language, key, value) {
    try {
      const response = await api.put(`/labels/${language}/${key}`, { value });
      if (response.success) {
        // Clear cache for this language
        this.cache.delete(`labels_${language}`);
        return true;
      } else {
        throw new Error('Failed to update label');
      }
    } catch (error) {
      console.error(`Failed to update label ${key} for language ${language}:`, error);
      return false;
    }
  }

  /**
   * Delete a specific label
   * @param {string} language - Language code
   * @param {string} key - Label key
   * @returns {Promise<boolean>} Success status
   */
  async deleteLabel(language, key) {
    try {
      const response = await api.delete(`/labels/${language}/${key}`);
      if (response.success) {
        // Clear cache for this language
        this.cache.delete(`labels_${language}`);
        return true;
      } else {
        throw new Error('Failed to delete label');
      }
    } catch (error) {
      console.error(`Failed to delete label ${key} for language ${language}:`, error);
      return false;
    }
  }

  /**
   * Save all labels for a language
   * @param {string} language - Language code
   * @param {Object} labels - Labels object
   * @returns {Promise<boolean>} Success status
   */
  async saveLabels(language, labels) {
    try {
      const response = await api.put(`/labels/${language}`, { labels });
      if (response.success) {
        // Clear cache for this language
        this.cache.delete(`labels_${language}`);
        return true;
      } else {
        throw new Error('Failed to save labels');
      }
    } catch (error) {
      console.error(`Failed to save labels for language ${language}:`, error);
      return false;
    }
  }

  /**
   * Export labels to JSON
   * @param {string} language - Language code
   * @returns {Promise<string>} JSON string
   */
  async exportLabels(language) {
    try {
      const response = await api.post(`/labels/${language}/export`, { format: 'json' });
      return response;
    } catch (error) {
      console.error(`Failed to export labels for language ${language}:`, error);
      return null;
    }
  }

  /**
   * Search labels
   * @param {string} language - Language code
   * @param {string} query - Search query
   * @returns {Promise<Array>} Search results
   */
  async searchLabels(language, query) {
    try {
      const response = await api.get(`/labels/${language}/search?q=${encodeURIComponent(query)}`);
      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to search labels');
      }
    } catch (error) {
      console.error(`Failed to search labels for language ${language}:`, error);
      return [];
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Clear cache for specific language
   * @param {string} language - Language code
   */
  clearCacheForLanguage(language) {
    this.cache.delete(`labels_${language}`);
  }
}

// Create singleton instance
const labelService = new LabelService();

export default labelService;
